/**
 * When the chat offers "Ja"/"Nein" under an answer.
 *
 * The assistant usually asks a question ("Soll ich … anlegen?") or asks for
 * confirmation ("Bitte bestätige kurz …") but often explains after it, so the
 * answer's LAST question or request counts, wherever it stands. A shown
 * proposal or change carries its own confirmation button ("So anlegen",
 * "Änderung übernehmen"); a second pair of buttons next to it would ask the
 * same thing twice. When several numbered options ("1. … 2. …") follow from
 * that question's paragraph on, the question asks which one is wanted, so a
 * bare "Ja" would not answer it. The backend's confirmation gate
 * (`agentChatTurn.ts`'s `asksForConfirmation`) mirrors this.
 */
// A shown choice or form view is itself the question's answer form, so it
// gets no separate yes/no pair either.
const OWN_CONFIRMATION = new Set([
  "template_proposal",
  "template_change",
  "choice",
  "form",
]);
/** The end of a word; `\b` only knows ASCII letters, not "ü", "ó" or Cyrillic. */
const WORD_END = String.raw`(?![\p{L}\p{N}])`;
/** Prepositions that may open an open question ("In welches …", "With whom …", "W jakim …"). */
const PREPOSITIONS = [
  // de
  "in|an|auf|aus|für|mit|bei|von|zu|nach|über|unter",
  // en
  "for|with|to|from|at|on|by|of|about|into",
  // fr ("d'" is handled on its own, it has no space after it)
  "à|de|dans|pour|avec|par|sur|en|chez|vers|sous|entre|depuis",
  // nl
  "op|aan|voor|met|bij|van|naar|over|uit|onder|door|tot|om",
  // pl
  "w|we|na|o|z|ze|do|od|dla|po|przy|za|przez|u|nad|pod",
  // ru
  "в|во|на|о|об|обо|с|со|к|ко|от|для|по|у|за|из|про|при|до",
].join("|");
/** Question words that ask for content, not yes/no. */
const QUESTION_WORDS = [
  // de
  "wer|wen|wem|wessen|welche[rsmn]?|wo|wohin|woher|wann|wie|warum|weshalb|wieso|wozu|womit|woran|worauf|wofür",
  // German "was", but not English "Was that/it … ?" (a yes/no question)
  String.raw`was(?!\s+(?:that|this|it|the|there|everything|anything|your|my|he|she|a|an)${WORD_END})`,
  // en
  "what|which|who|whom|whose|where|when|why|how",
  // fr ("qu'" is handled on its own)
  "que|quoi|quel(?:le)?s?|qui|où|quand|pourquoi|comment|combien|(?:le|la|les|au|aux|du|des)quel(?:le)?s?",
  // nl
  "wat|welke?|wie|wiens|waar|waarheen|waarvandaan|wanneer|waarom|hoe|hoeveel|waar(?:mee|voor|over|op|in|aan|naar|door|bij|uit|om|van)",
  // pl
  "co|czego|czemu|czym|jak(?:i|a|ie|iego|iej|iemu|im|imi|ich|ą)?|któr(?:y|a|e|ego|ej|emu|ym|ymi|ych|ą)|kto|kogo|komu|kim|gdzie|dokąd|skąd|kiedy|dlaczego|ile|ilu",
  // ru
  "что|чего|чему|чем|чём|как(?:ой|ая|ое|ие|ого|ую|ом|ому|им|ими|их)?|котор(?:ый|ая|ое|ые|ого|ую|ом|ому|ым|ыми|ых|ой)|кто|кого|кому|кем|ком|чей|чья|чьё|чьи|где|куда|откуда|когда|почему|зачем|сколько",
].join("|");
/**
 * An open question ("Welches Projekt?", "How can I help?", "Comment puis-je
 * vous aider ?", "Чем я могу помочь?") asks for content, not yes/no. The
 * question word opens the sentence, possibly after a preposition.
 */
const OPEN_QUESTION = new RegExp(
  String.raw`^(?:(?:${PREPOSITIONS})\s+|d['’]\s*)?(?:(?:${QUESTION_WORDS})${WORD_END}|qu['’](?=\p{L}))`,
  "iu"
);
/** An explicit request for confirmation ("Bitte bestätige kurz …", "Please confirm …") opens its sentence. */
const CONFIRMATION_REQUEST = new RegExp(
  String.raw`^(?:bitte\s+)?(?:bestätige|bestätigen\s+sie|(?:please\s+)?confirm)${WORD_END}`,
  "iu"
);
/**
 * Sentence boundary: whitespace after .!?: and any closing emphasis, quote or
 * bracket ("…?** Ich warte …"), or a line break — mirrors (never imports; a
 * different app) the backend's `SENTENCE_SPLIT` in `agentReplyGuard.ts`.
 */
const SENTENCE_SPLIT = /(?<=[.!?:][*_)"“”»]*)\s+|\n+/;
/** The words for "or" that join two alternatives. */
const OR_WORD = new RegExp(
  String.raw`(?<![\p{L}\p{N}])(?:or|oder|ou|of|lub|albo|czy|или|либо)${WORD_END}`,
  "giu"
);
/** "… or not?" ("oder nicht?", "ou pas ?", "или нет?") still asks yes/no. */
const ONLY_NEGATION = /^(?:not|nicht|non|pas|niet|nie|нет)$/iu;
/** Dutch "of" after these verbs means "whether": "Zal ik controleren of …?". */
const DUTCH_WHETHER_VERB =
  /(?:^|\s)(?:kijk|kijken|controleer|controleren|check|checken|weet|weten|vraag|vragen|zie|zien|bekijk|bekijken|nagaan|test|testen)\s*,?\s*$/iu;

/**
 * An alternative question ("Would you like A or B?", "Soll ich A oder B …?")
 * is answered by picking one, not by yes/no. A bare "or" tag at the end
 * ("Das passt, oder?") or "… or not?" keeps it a yes/no question, as does
 * "or" opening the sentence ("Oder soll ich …?", "Czy mam …?").
 */
function isAlternativeQuestion(question: string): boolean {
  for (const match of question.matchAll(OR_WORD)) {
    const index = match.index;
    if (index === 0) continue;
    const before = question.slice(0, index);
    const rest = question
      .slice(index + match[0].length)
      .replace(/[\s?*_)"“”»«.,!…]+/gu, " ")
      .trim();
    if (rest === "" || ONLY_NEGATION.test(rest)) continue;
    const word = match[0].toLowerCase();
    if (word === "of" && DUTCH_WHETHER_VERB.test(before)) continue;
    // Polish ", czy …" is "whether": "Czy mam sprawdzić, czy cena …?"
    if (word === "czy" && /,\s*$/.test(before)) continue;
    return true;
  }
  return false;
}

export function offersYesNo(
  content: string,
  viewComponents: readonly string[]
): boolean {
  if (viewComponents.some(c => OWN_CONFIRMATION.has(c))) return false;
  const paragraphs = content.trim().split(/\n\s*\n/);
  for (let p = paragraphs.length - 1; p >= 0; p--) {
    // French puts a (narrow) no-break space before "?", which `trim()` and
    // `\s` both cover.
    const sentences = (paragraphs[p] ?? "")
      .split(SENTENCE_SPLIT)
      .map(sentence => sentence.trim().replace(/^[\s*_"„“»«‘']+/, ""));
    for (let i = sentences.length - 1; i >= 0; i--) {
      const sentence = sentences[i] ?? "";
      const isQuestion = /\?[\s*_)"“”»]*$/.test(sentence);
      if (!isQuestion && !CONFIRMATION_REQUEST.test(sentence)) continue;
      // An open question ("In welches Projekt …?", "How can I help?") or one
      // offering alternatives ("A oder B?") has no yes/no answer.
      if (
        isQuestion &&
        (OPEN_QUESTION.test(sentence) || isAlternativeQuestion(sentence))
      )
        return false;
      // Several numbered options ("1. … 2. …") ask which one is wanted; a
      // bare "Ja" would not pick any of them. A single numbered line is still
      // an ordinary yes/no question if it is one.
      const fromHere = paragraphs.slice(p).join("\n\n");
      return (fromHere.match(/^\s*\d+[.)]\s/gm) ?? []).length < 2;
    }
  }
  return false;
}

/** Actions that are hard to undo or touch who may do what. */
const RISKY =
  /(lösch|entfern|veröffentlich|publizier|überschreib|unwiderruflich|endgültig|rolle|recht|berechtigung|zugang|kündig|deaktivier)/i;

/**
 * The question of an answer that asks for something hard to undo (deleting,
 * rights, publishing), or `null`. Such a confirmation is shown as a
 * warning; an ordinary yes/no stays a quiet pair of buttons.
 */
export function riskyQuestion(content: string): string | null {
  const questions = content
    .trim()
    .split(SENTENCE_SPLIT)
    .map(s => s.replace(/^[\s*_"„“»]+|[\s*_)"“”»]+$/g, ""))
    .filter(s => s.endsWith("?"));
  const last = questions[questions.length - 1];
  return last && RISKY.test(last) ? last : null;
}
